import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { ArrowRight, Search, Tv } from "lucide-react";

import ArchiveTasteSearchForm from "./ArchiveTasteSearchForm";
import ShowPicker from "./show-picker/ShowPicker";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";
import { getArchiveTastePrompts } from "../services/search/archiveTastePrompts";
import { ARCHIVE_SEARCH_INDEX_SCOPES, loadArchiveSearchIndex } from "../services/search/archiveSearchIndex";
import {
    getRecommendationDestination,
    getRecommendationSuggestionMeta,
    isRecommendationAnchor,
    mergeRecommendationCatalog,
} from "../pages/most-recommended/recommendationHub";
import { handleInternalLinkClick } from "../utils/navigation";
import { trackEvent } from "../utils/analytics";

const HERO_SEARCH_PROMPTS = getArchiveTastePrompts("homepageHero");

const HERO_TABS = [
    { id: "mood", label: "Describe a mood", shortLabel: "By mood", icon: Search },
    { id: "show", label: "Start from a show", shortLabel: "From a show", icon: Tv },
];

// The live example is a real "liked X, try Y" pairing. It only appears when it
// can explain itself and lead somewhere: two different shows with posters, a
// reason, and a shows-like page for X.
export function isHeroExamplePairing(pairing) {
    const anchor = pairing?.anchor;
    const current = pairing?.current;
    return Boolean(
        anchor?.slug
        && current?.slug
        && anchor.slug !== current.slug
        && anchor.poster
        && current.poster
        && anchor.hasShowsLikePage === true
        && typeof pairing.bridge === "string"
        && pairing.bridge.trim()
    );
}

// "Start from a show": the same picker and rules as Recommendations, so it only
// offers shows with a shows-like page. Mounted when the tab is first opened, so
// the show list loads only for people who use it.
const HeroShowPicker = () => {
    const [state, setState] = useState({ status: "loading", shows: [] });
    const activeRef = useRef(true);

    const fetchShows = useCallback(() => {
        loadArchiveSearchIndex({ scope: ARCHIVE_SEARCH_INDEX_SCOPES.ALL })
            .then((index) => {
                if (activeRef.current) {
                    setState({ status: "ready", shows: mergeRecommendationCatalog(index?.shows || [], []) });
                }
            })
            .catch(() => {
                if (!activeRef.current) return;
                setState({ status: "error", shows: [] });
                trackEvent("recommendation_index_error", { category: "load_failed", surface: "homepage_hero" });
            });
    }, []);

    useEffect(() => {
        activeRef.current = true;
        fetchShows();
        return () => {
            activeRef.current = false;
        };
    }, [fetchShows]);

    const retry = () => {
        setState({ status: "loading", shows: [] });
        fetchShows();
    };

    return (
        <div className="space-y-2">
            <p className="text-body-small text-muted-foreground">Recommendations cover recent premieres and returning shows, mostly from the past year.</p>
            <ShowPicker
                id="homepage-hero-show-picker"
                label="Find shows like a favourite"
                placeholder="Search for a show"
                shows={state.shows}
                loading={state.status === "loading"}
                disabled={state.status !== "ready"}
                isEligible={isRecommendationAnchor}
                getSuggestionMeta={getRecommendationSuggestionMeta}
                getDestinationLabel={() => "See matches"}
                getHref={getRecommendationDestination}
                noResultsMessage="No recommendation page for that title."
                getNoResultsHref={(query) => `/archive?q=${encodeURIComponent(query)}&mode=lexical`}
                onSelect={(show, event) => {
                    const href = getRecommendationDestination(show);
                    if (!href) return;
                    trackEvent("homepage_hero_show_pick", { show_slug: show.slug });
                    handleInternalLinkClick(event, href);
                }}
            />
            {state.status === "error" ? (
                <p role="status" className="text-body-small text-destructive">
                    Show search didn&apos;t load.{" "}
                    <button type="button" onClick={retry} className="font-semibold underline underline-offset-2">
                        Try again
                    </button>
                </p>
            ) : null}
        </div>
    );
};

const HeroExample = ({ pairing }) => {
    const { anchor, current } = pairing;
    // The connection to the pick can be in sentence two. Keep the complete
    // published reason so the example actually explains the recommendation.
    const bridge = pairing.bridge.trim();
    const track = (target) => trackEvent("homepage_hero_example_click", {
        target,
        anchor_slug: anchor.slug,
        current_slug: current.slug,
    });

    return (
        <aside className="hero-example" aria-labelledby="hero-example-title">
            <div className="hero-example__poster-labels"><span>Liked</span><span>Try next</span></div>
            <div className="hero-example__stage">
                <a
                    href={`/show/${anchor.slug}`}
                    className="hero-example__poster hero-example__poster--liked"
                    onClick={(event) => { track("anchor"); handleInternalLinkClick(event, `/show/${anchor.slug}`); }}
                >
                    <img src={anchor.poster} alt={`${anchor.title} poster`} loading="lazy" />
                </a>
                <a
                    href={`/show/${current.slug}`}
                    className="hero-example__poster hero-example__poster--try"
                    onClick={(event) => { track("current"); handleInternalLinkClick(event, `/show/${current.slug}`); }}
                >
                    <img src={current.poster} alt={`${current.title} poster`} loading="lazy" />
                </a>
            </div>
            <h2 id="hero-example-title" className="hero-example__title">
                Liked {anchor.title}? Try {current.title}.
            </h2>
            <p className="hero-example__bridge">{bridge}</p>
            <a
                href={`/shows-like/${anchor.slug}`}
                className="hero-example__more"
                onClick={(event) => { track("more"); handleInternalLinkClick(event, `/shows-like/${anchor.slug}`); }}
            >
                See more shows like {anchor.title}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
        </aside>
    );
};

// Phones and tablets get the same example, with the whole published reason.
const HeroExampleGlimpse = ({ pairing }) => {
    const { anchor, current } = pairing;
    const href = `/shows-like/${anchor.slug}`;
    const track = (target) => trackEvent("homepage_hero_example_click", {
        target,
        anchor_slug: anchor.slug,
        current_slug: current.slug,
    });

    return (
        <aside className="hero-glimpse lg:hidden" aria-labelledby="hero-glimpse-title">
            <div className="hero-glimpse__head">
                <span className="hero-glimpse__posters" aria-hidden="true">
                    <img src={anchor.poster} alt="" loading="lazy" />
                    <img src={current.poster} alt="" loading="lazy" />
                </span>
                <h2 id="hero-glimpse-title" className="hero-glimpse__title">
                    Liked {anchor.title}? Try {current.title}.
                </h2>
            </div>
            <p className="hero-glimpse__bridge">{pairing.bridge.trim()}</p>
            <a
                href={href}
                className="hero-glimpse__more"
                onClick={(event) => { track("glimpse"); handleInternalLinkClick(event, href); }}
            >
                See more shows like {anchor.title}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
        </aside>
    );
};

const pairingShowShape = PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    poster: PropTypes.string,
    hasShowsLikePage: PropTypes.bool,
});

const pairingShape = PropTypes.shape({
    anchor: pairingShowShape.isRequired,
    current: pairingShowShape.isRequired,
    bridge: PropTypes.string,
});

HeroExample.propTypes = {
    pairing: pairingShape.isRequired,
};

HeroExampleGlimpse.propTypes = {
    pairing: pairingShape.isRequired,
};

const ValueProposition = ({
    examplePairing = null,
    ...props
}) => {
    const prefersReducedMotion = usePrefersReducedMotion();
    const [activeTab, setActiveTab] = useState("mood");
    const [showTabOpened, setShowTabOpened] = useState(false);
    const tabRefs = useRef({});

    const selectTab = useCallback((tabId, { focus = false } = {}) => {
        setActiveTab(tabId);
        if (tabId === "show") setShowTabOpened(true);
        if (focus) tabRefs.current[tabId]?.focus();
        trackEvent("homepage_hero_mode_select", { mode: tabId });
    }, []);

    const handleTabKeyDown = useCallback((event) => {
        const index = HERO_TABS.findIndex((tab) => tab.id === activeTab);
        const lastIndex = HERO_TABS.length - 1;
        const nextIndex = {
            ArrowRight: index === lastIndex ? 0 : index + 1,
            ArrowLeft: index === 0 ? lastIndex : index - 1,
            Home: 0,
            End: lastIndex,
        }[event.key];
        if (nextIndex === undefined) return;
        event.preventDefault();
        selectTab(HERO_TABS[nextIndex].id, { focus: true });
    }, [activeTab, selectTab]);

    return (
        <section
            className="hero-section relative -mt-14 left-1/2 right-1/2 mb-5 w-screen -ml-[50vw] -mr-[50vw] overflow-x-clip border-b border-border/50 bg-body-gradient pb-6 pt-6 sm:-mt-20 sm:mb-8 sm:pb-11 sm:pt-11"
            {...props}
        >
            {/* Decoration is clipped on its own layer, so the show picker's
                suggestion list can extend past the hero. */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-1 bg-primary" />
                <div className="hero-wash absolute inset-0" />
                {/* Today's pick tints the hero behind the example (wide screens). */}
                {examplePairing ? (
                    <img src={examplePairing.current.poster} alt="" className="hero-backdrop" />
                ) : null}
            </div>

            <div className="relative z-20 mx-auto max-w-7xl px-6 sm:px-8 lg:px-10">
                <motion.div
                    className="hero-instrument"
                    initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.48, delay: 0.12, ease: [0.25, 1, 0.5, 1] }}
                >
                    <div className="hero-instrument__content">
                        <div className="space-y-2 sm:space-y-3">
                            <h1 className="hero-home-title text-balance text-foreground">
                                New TV and your next watch
                            </h1>
                            <p className="max-w-2xl text-body-large leading-relaxed text-muted-foreground sm:text-body-xl">
                                <span className="hidden sm:inline">New and returning TV worth your time, and what to watch after the show you just finished.</span>
                                <span className="sm:hidden">Upcoming scripted TV and shows like your favourites.</span>
                            </p>
                        </div>

                        <div className="hero-instrument__search">
                            <div
                                role="tablist"
                                aria-label="Ways to find your next show"
                                className="hero-tabs"
                                data-active={activeTab}
                                onKeyDown={handleTabKeyDown}
                            >
                                {HERO_TABS.map(({ id, label, shortLabel, icon: Icon }) => (
                                    <button
                                        key={id}
                                        ref={(node) => { tabRefs.current[id] = node; }}
                                        type="button"
                                        role="tab"
                                        id={`hero-tab-${id}`}
                                        aria-selected={activeTab === id}
                                        aria-controls={`hero-panel-${id}`}
                                        tabIndex={activeTab === id ? 0 : -1}
                                        className="hero-tab"
                                        onClick={() => selectTab(id)}
                                    >
                                        <Icon className="h-4 w-4" aria-hidden="true" />
                                        <span className="sm:hidden">{shortLabel}</span>
                                        <span className="hidden sm:inline">{label}</span>
                                    </button>
                                ))}
                            </div>

                            <div
                                role="tabpanel"
                                id="hero-panel-mood"
                                aria-labelledby="hero-tab-mood"
                                hidden={activeTab !== "mood"}
                            >
                                <ArchiveTasteSearchForm
                                    id="homepage-hero-taste-search"
                                    prompts={HERO_SEARCH_PROMPTS}
                                    placeholder="Try “cosy village mystery”"
                                    submitLabel="Search"
                                    surface="homepage_hero_instrument"
                                    promptsClassName="hidden sm:flex flex-nowrap gap-2 overflow-x-auto pb-1 *:shrink-0 *:whitespace-nowrap sm:flex-wrap sm:overflow-visible sm:pb-0"
                                />
                            </div>

                            <div
                                role="tabpanel"
                                id="hero-panel-show"
                                aria-labelledby="hero-tab-show"
                                hidden={activeTab !== "show"}
                            >
                                {showTabOpened ? <HeroShowPicker /> : null}
                            </div>
                        </div>

                        {examplePairing ? <HeroExampleGlimpse pairing={examplePairing} /> : null}
                    </div>

                    {examplePairing ? <HeroExample pairing={examplePairing} /> : null}
                </motion.div>
            </div>
        </section>
    );
};

ValueProposition.propTypes = {
    examplePairing: pairingShape,
};

export default ValueProposition;
